import { ReactElement, useMemo, useState } from 'react';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import useEventUsgaPostBatches from '../../dataHooks/useEventUsgaPostBatches';
import useUsgaSettings from '../../dataHooks/useUsgaSettings';
import { useUsgaPostMutators } from '../../dataMutators/useUsgaPostMutators';
import { EVENT_SCORE_ENTRY_MODES, EVENT_SCORE_STATUSES } from '../../types/EventScore';
import { USGA_POST_ACTIONS, USGA_SCORE_TYPES } from '../../types/UsgaPost';
import { resolvePlayerTeeId } from '../../utils/handicapUtils';
import { usgaPostRowAction, usgaPostRowStatus } from '../../utils/usgaPostStatus';
import { buildUsgaPostSent, holeNumbersForDay, startingHoleForPlayer, usgaPlayedAt } from '../../utils/usgaScorePostUtils';
import GratiSection from '../common/GratiSection';

import EventScoreUsgaPostAllForm from './EventScoreUsgaPostAllForm';
import EventScoreUsgaPostRow from './EventScoreUsgaPostRow';

import type { CourseTeeSnapshot } from '../../types/Course';
import type Event from '../../types/Event';
import type { EventScore } from '../../types/EventScore';
import type Registration from '../../types/Registration';
import type { TeeSheet } from '../../types/TeeSheet';
import type { UsgaPostChoices } from './EventScoreUsgaPostForm';
import type { UsgaPostRow } from './EventScoreUsgaPostRow';

/** The open panel's id when it's Post all rather than a row (one panel open at a time). */
const POST_ALL = '__postAll__';

/** Not-handicapped sentinel stored in numeric handicap fields (shared/utils/ghinIndex.ts). */
const NH_HANDICAP_INDEX = 999;

interface EventScoreUsgaPostingProps {
  event: Event;
  dayIndex: number;
  /** The day's scores, already loaded by the score section. */
  scores: EventScore[];
  attendeeList: Registration[];
  teeSheet?: TeeSheet;
}

/**
 * God-only "Post to USGA (staging)" section under a day's scores: one row per score, showing its
 * posting status, what would be sent, and its action (post, post again, confirm). Built from data
 * the score section already loaded; the only reads here are the event's pending post requests and
 * the USGA settings. Posting writes a request (usgaPostBatch) that the server runs.
 */
export default function EventScoreUsgaPosting({ event, dayIndex, scores, attendeeList, teeSheet }: EventScoreUsgaPostingProps): ReactElement {
  const { pendingBatches } = useEventUsgaPostBatches(event.orgId, event.itemId);
  const { settings } = useUsgaSettings();
  const { requestUsgaPosts } = useUsgaPostMutators();
  const [openScoreId, setOpenScoreId] = useState<string | null>(null);

  const requestedScoreIds = useMemo(
    () => new Set(pendingBatches.flatMap((b) => b.items.map((item) => item.scoreId))),
    [pendingBatches],
  );

  const rows = useMemo((): UsgaPostRow[] => {
    const day = event.days[dayIndex];
    const coursePair = event.coursePairs?.find((cp) => cp.id === day?.courseId);
    const snapshots = (coursePair?.teeSnapshots ?? day?.teeSnapshots ?? {}) as Record<string, CourseTeeSnapshot>;
    const regsById = new Map(attendeeList.map((reg) => [reg.playerId, reg]));
    const playedAt = day?.datetime && 'toDate' in day.datetime
      ? usgaPlayedAt(day.datetime.toDate(), coursePair?.timezone ?? event.timezone)
      : '';
    const now = Date.now();

    // Only scores of players still in the event, the same as the score grid above. A removed
    // player's (or deleted guest's) score doc can be left behind.
    return scores
      .filter((score) => score.status !== EVENT_SCORE_STATUSES.DELETED)
      .filter((score) => score.playerIds.length > 0 && score.playerIds.every((id) => regsById.has(id)))
      .map((score) => {
        const playerId = score.playerIds[0];
        const reg = regsById.get(playerId);
        const index = score.ghinIndexOverride ?? score.ghinIndex ?? reg?.ghinIndex;
        const context = {
          score,
          day,
          startingHole: startingHoleForPlayer(teeSheet, playerId),
          golfer: { ghin: reg?.ghin, gender: reg?.gender, handicapIndex: index == null || index === NH_HANDICAP_INDEX ? null : index },
          tee: snapshots[score.teeId ?? resolvePlayerTeeId(playerId, reg, teeSheet) ?? ''],
          usgaCourseId: coursePair?.usgaCourseId,
          playedAt,
          scoreType: USGA_SCORE_TYPES.HOME, // chosen when posting; doesn't affect the preview
          season: coursePair?.usgaSeason ?? null,
        };
        const build = buildUsgaPostSent(context);
        const isRequested = requestedScoreIds.has(score.itemId);
        // Gratigolf doesn't know this golfer's index (unknown, not NH): the server asks the USGA for
        // it when posting, so a bare X's value isn't decided yet.
        const xHolesFromUsgaIndex = index == null && day
          ? new Set(holeNumbersForDay(day).filter((_, i) => score.scoreByHole[i] === -1))
          : new Set<number>();
        return {
          scoreId: score.itemId,
          name: score.playerNames.join(' & '),
          ghin: reg?.ghin,
          build,
          status: usgaPostRowStatus({ post: score.usgaPost, isRequested, build, now, accessWaitDays: settings.accessWaitDays }),
          action: usgaPostRowAction({ post: score.usgaPost, isRequested, build, now }),
          xHolesFromUsgaIndex,
          reviewMessage: score.usgaPost?.validationMessage ?? null,
          isTotalOnly: score.entryMode === EVENT_SCORE_ENTRY_MODES.TOTAL_ONLY,
          defaultAdjustedScore: score.totalScore,
          defaultHolesPlayed: day?.numHoles ?? 18,
          lastScoreType: score.usgaPost?.requestedScoreType ?? null,
          preview: (choices: Omit<UsgaPostChoices, 'scoreType'>) => buildUsgaPostSent({ ...context, ...choices }),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [event, dayIndex, scores, attendeeList, teeSheet, requestedScoreIds, settings.accessWaitDays]);

  // Post all: every score that can be posted as it is. Total-only scores need their adjusted
  // score confirmed, and an off-season day's "send anyway" is a single-score test — those, and
  // scores needing Post again, are posted one at a time.
  const readyRows = rows.filter((r) => r.action === 'post' && r.build.ok && !r.isTotalOnly);
  const totalOnlyCount = rows.filter((r) => r.action === 'post' && r.build.ok && r.isTotalOnly).length;
  const isPostAllOpen = openScoreId === POST_ALL;

  return (
    <GratiSection headerStyle="outlined" title="Post to USGA (staging)" collapsible defaultExpanded={false}>
      {rows.length === 0 ? (
        <Typography variant="bodySmall" color="text.secondary">No scores for this day yet.</Typography>
      ) : (
        <Stack spacing={1}>
          {isPostAllOpen ? (
            <EventScoreUsgaPostAllForm
              readyCount={readyRows.length}
              totalOnlyCount={totalOnlyCount}
              onSubmit={async (scoreType) => {
                await requestUsgaPosts(event, readyRows.map((r) => ({
                  scoreId: r.scoreId,
                  action: USGA_POST_ACTIONS.POST,
                  scoreType,
                  adjustedGrossOverride: null,
                  holesPlayedOverride: null,
                })));
                setOpenScoreId(null);
              }}
              onCancel={() => setOpenScoreId(null)}
            />
          ) : (
            <Stack direction="row">
              <Button size="small" variant="outlined" disabled={readyRows.length === 0} onClick={() => setOpenScoreId(POST_ALL)}>
                Post all ready ({readyRows.length})
              </Button>
            </Stack>
          )}
          {rows.map((row) => (
            <EventScoreUsgaPostRow
              key={row.scoreId}
              orgId={event.orgId}
              eventId={event.itemId}
              row={row}
              isOpen={openScoreId === row.scoreId}
              onOpen={() => setOpenScoreId(row.scoreId)}
              onClose={() => setOpenScoreId((id) => (id === row.scoreId ? null : id))}
              onPost={(choices) => requestUsgaPosts(event, [{
                scoreId: row.scoreId,
                action: USGA_POST_ACTIONS.POST,
                scoreType: choices.scoreType,
                adjustedGrossOverride: choices.adjustedGrossOverride,
                holesPlayedOverride: choices.holesPlayedOverride,
              }], choices.skipSeasonCheck)}
              onConfirm={() => requestUsgaPosts(event, [{
                scoreId: row.scoreId,
                action: USGA_POST_ACTIONS.CONFIRM,
                scoreType: row.lastScoreType ?? USGA_SCORE_TYPES.HOME,
                adjustedGrossOverride: null,
                holesPlayedOverride: null,
              }])}
            />
          ))}
        </Stack>
      )}
    </GratiSection>
  );
}
